import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Search, MapPin, Phone, Globe, Star, Download, Loader2,
  Building2, ExternalLink, Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";


const CATEGORIES = [
  "Restaurants", "Hotels", "Pubs", "Schools", "Offices",
  "Care Homes", "Leisure Centres", "Nurseries", "Hospitals",
  "Shopping Centres", "Factories", "Warehouses", "Gyms",
  "Community Centres", "Churches", "Universities",
];

const SERVICE_TYPES = [
  "Boiler Installation", "Gas Safety Inspections", "Commercial Heating Systems",
  "Pipework", "Maintenance Contracts", "Emergency Repairs",
  "Ventilation Systems", "Gas Appliance Servicing",
];

const RADIUS_OPTIONS = [
  { value: "1000", label: "1 km" },
  { value: "2000", label: "2 km" },
  { value: "5000", label: "5 km" },
  { value: "10000", label: "10 km" },
  { value: "20000", label: "20 km" },
  { value: "50000", label: "50 km" },
];

// Mobile business card
const MobileBusinessCard = ({ biz, selected, onToggle }) => (
  <div
    data-testid={`scraper-mobile-card-${biz.place_id}`}
    className="p-4 rounded-sm border border-white/5 bg-card hover:bg-white/[0.03] transition-colors duration-150"
  >
    <div className="flex items-start gap-3">
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggle(biz.place_id)}
        className="mt-1 flex-shrink-0 border-white/20"
      />
      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-sm font-semibold text-foreground">{biz.name}</p>
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-muted-foreground truncate">{biz.address}</span>
        </div>
        {biz.phone && (
          <a href={`tel:${biz.phone}`} className="flex items-center gap-1 text-blue-400 hover:text-blue-300">
            <Phone className="w-3 h-3" />
            <span className="text-xs">{biz.phone}</span>
          </a>
        )}
        <div className="flex items-center gap-3">
          {biz.rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
              <span className="text-xs text-amber-400">{biz.rating}</span>
            </div>
          )}
          {biz.website && (
            <a href={biz.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-400 hover:text-blue-300">
              <Globe className="w-3 h-3" /><span className="text-xs">Website</span>
            </a>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default function ScraperPage() {
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("Restaurants");
  const [radius, setRadius] = useState("5000");
  const [serviceType, setServiceType] = useState("Gas Appliance Servicing");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lastImported, setLastImported] = useState([]);
  const [enrichingAll, setEnrichingAll] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState({ done: 0, total: 0 });

  const handleSearch = async () => {
    if (!location.trim()) {
      toast.error("Please enter a location");
      return;
    }
    setSearching(true);
    setResults(null);
    setSelected(new Set());
    try {
      const res = await api.post('/scraper/search', {
        category,
        location: location.trim(),
        radius: parseInt(radius),
      }, { timeout: 120000 });
      setResults(res.data);
      if (res.data.count === 0) {
        toast.info("No businesses found. Try a different location or category.");
      } else {
        toast.success(`Found ${res.data.count} businesses`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Search failed. Check your connection and try again.";
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const toggleSelect = (placeId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!results?.businesses) return;
    if (selected.size === results.businesses.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.businesses.map(b => b.place_id)));
    }
  };

  const handleEnrichAll = async () => {
    const withWebsite = lastImported.filter(l => l.website);
    if (withWebsite.length === 0) {
      toast.info("None of the imported leads have a website — enrichment requires a website URL.");
      return;
    }
    setEnrichingAll(true);
    setEnrichProgress({ done: 0, total: withWebsite.length });
    let succeeded = 0;
    for (const lead of withWebsite) {
      try {
        await api.post(`/leads/${lead.id}/enrich`);
        succeeded++;
      } catch { /* skip failed */ }
      setEnrichProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setEnrichingAll(false);
    setLastImported([]);
    toast.success(`Enriched ${succeeded} of ${withWebsite.length} leads`);
  };

  const handleImport = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one business to import");
      return;
    }
    setImporting(true);
    try {
      const businesses = results.businesses.filter(b => selected.has(b.place_id));
      const res = await api.post('/scraper/import', {
        businesses,
        service_type: serviceType,
      });
      toast.success(`Imported ${res.data.imported_count} leads. ${res.data.skipped_count} duplicates skipped.`);
      setLastImported(res.data.leads || []);
      setSelected(new Set());
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="scraper-page">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Lead Finder
        </h1>
        <p className="text-sm md:text-base text-muted-foreground mt-1">
          Search Google Places for local businesses and import them as leads
        </p>
      </div>

      {/* Search Form */}
      <Card className="border-white/5 bg-card rounded-sm mb-6" data-testid="scraper-search-form">
        <CardContent className="p-4 md:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="scraper-location"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="e.g. Manchester, London SW1"
                  className="pl-9 h-10 bg-secondary/50 border-white/5 rounded-sm"
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="scraper-category" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs tracking-wider uppercase text-muted-foreground">Radius</Label>
              <Select value={radius} onValueChange={setRadius}>
                <SelectTrigger data-testid="scraper-radius" className="h-10 bg-secondary/50 border-white/5 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RADIUS_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 flex flex-col justify-end">
              <Button
                data-testid="scraper-search-btn"
                onClick={handleSearch}
                disabled={searching}
                className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-xs h-10"
              >
                {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {searching ? "Searching..." : "Find Businesses"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Post-import enrichment banner */}
      {lastImported.length > 0 && !enrichingAll && (
        <div className="flex items-center justify-between gap-4 mb-4 p-4 rounded-sm border border-blue-500/20 bg-blue-500/5 animate-fade-slide-up">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <p className="text-sm text-foreground">
              <span className="font-semibold">{lastImported.length} leads imported.</span>{" "}
              <span className="text-muted-foreground">
                Enrich them with AI to find emails and key contacts from their websites.
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setLastImported([])}
              className="rounded-sm text-xs text-muted-foreground h-8 px-3"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={handleEnrichAll}
              className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white text-xs h-8 px-4"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Enrich All
            </Button>
          </div>
        </div>
      )}

      {enrichingAll && (
        <div className="flex items-center gap-3 mb-4 p-4 rounded-sm border border-white/5 bg-card animate-pulse">
          <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
          <p className="text-sm text-muted-foreground">
            Enriching leads... {enrichProgress.done}/{enrichProgress.total}
          </p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="animate-fade-slide-up">
          {/* Results Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {results.count} RESULTS
              </h2>
              <Badge variant="outline" className="text-[10px] rounded-sm">{results.category}</Badge>
              <span className="text-xs text-muted-foreground">{results.location}</span>
            </div>
            {results.count > 0 && (
              <div className="flex items-center gap-3">
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger data-testid="scraper-import-service" className="w-[200px] h-9 bg-secondary/50 border-white/5 rounded-sm text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="scraper-import-btn"
                  onClick={handleImport}
                  disabled={importing || selected.size === 0}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider text-xs h-9 px-4"
                >
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Import {selected.size > 0 ? `(${selected.size})` : ""}
                </Button>
              </div>
            )}
          </div>

          {results.count === 0 ? (
            <Card className="border-white/5 bg-card rounded-sm">
              <CardContent className="p-12 text-center">
                <p className="text-sm text-muted-foreground">No businesses found. Try a different location or category.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Desktop Table */}
              <Card className="border-white/5 bg-card rounded-sm hidden md:block" data-testid="scraper-results-table">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selected.size === results.businesses.length}
                          onCheckedChange={toggleAll}
                          className="border-white/20"
                          data-testid="scraper-select-all"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-bold tracking-widest uppercase">Business</TableHead>
                      <TableHead className="text-xs font-bold tracking-widest uppercase">Address</TableHead>
                      <TableHead className="text-xs font-bold tracking-widest uppercase">Phone</TableHead>
                      <TableHead className="text-xs font-bold tracking-widest uppercase">Website</TableHead>
                      <TableHead className="text-xs font-bold tracking-widest uppercase text-center">Rating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.businesses.map((biz, i) => (
                      <TableRow
                        key={biz.place_id}
                        data-testid={`scraper-row-${biz.place_id}`}
                        className={cn(
                          "border-white/5 hover:bg-white/[0.03] animate-fade-slide-up",
                          selected.has(biz.place_id) && "bg-blue-500/5"
                        )}
                        style={{ animationDelay: `${i * 30}ms` }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selected.has(biz.place_id)}
                            onCheckedChange={() => toggleSelect(biz.place_id)}
                            className="border-white/20"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-sm bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-3.5 h-3.5 text-blue-400" strokeWidth={1.5} />
                            </div>
                            <span className="font-medium text-sm">{biz.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{biz.address}</TableCell>
                        <TableCell>
                          {biz.phone ? (
                            <a href={`tel:${biz.phone}`} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid={`scraper-phone-${biz.place_id}`}>
                              <Phone className="w-3 h-3" />{biz.phone}
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {biz.website ? (
                            <a href={biz.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />Visit
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {biz.rating > 0 ? (
                            <div className="flex items-center justify-center gap-1">
                              <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                              <span className="text-xs text-amber-400">{biz.rating}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3" data-testid="scraper-mobile-results">
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox
                    checked={selected.size === results.businesses.length}
                    onCheckedChange={toggleAll}
                    className="border-white/20"
                  />
                  <span className="text-xs text-muted-foreground">Select all</span>
                </div>
                {results.businesses.map(biz => (
                  <MobileBusinessCard
                    key={biz.place_id}
                    biz={biz}
                    selected={selected.has(biz.place_id)}
                    onToggle={toggleSelect}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
